/**
 * API Key Service
 *
 * CRUD operations for workspace API Keys used by external integrations.
 *
 * Note: The backend endpoints return data directly (no { success, message, data } wrapper).
 * workspace_id is passed via the X-Workspace-Id header (read by get_current_workspace_id dep).
 */

import axiosInstance, { extractErrorMessage } from './axiosConfig';
import { ApiKey, ApiKeyCreateRequest, ApiKeyCreateResponse, ApiKeyUpdateRequest } from '../types/apiKey';
import { AxiosError } from 'axios';

/** Build headers with workspace context */
const wsHeaders = (workspaceId: number) => ({ 'X-Workspace-Id': String(workspaceId) });

/**
 * List all API Keys for a workspace
 */
export const listApiKeys = async (workspaceId: number): Promise<ApiKey[]> => {
  try {
    const response = await axiosInstance.get<ApiKey[]>(`/api-keys`, {
      headers: wsHeaders(workspaceId),
    });
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error as AxiosError, 'Falha ao listar chaves de API'));
  }
};

/**
 * Create a new API Key for a workspace.
 * The response includes `api_secret` — store it immediately, it won't be shown again.
 */
export const createApiKey = async (
  workspaceId: number,
  data: ApiKeyCreateRequest
): Promise<ApiKeyCreateResponse> => {
  try {
    const response = await axiosInstance.post<ApiKeyCreateResponse>(`/api-keys`, data, {
      headers: wsHeaders(workspaceId),
    });
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error as AxiosError, 'Falha ao criar chave de API'));
  }
};

/**
 * Update an existing API Key (name, is_active, expires_at, notes, max_tokens, reset_tokens)
 */
export const updateApiKey = async (
  workspaceId: number,
  keyId: number,
  data: ApiKeyUpdateRequest
): Promise<ApiKey> => {
  try {
    const response = await axiosInstance.patch<ApiKey>(`/api-keys/${keyId}`, data, {
      headers: wsHeaders(workspaceId),
    });
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error as AxiosError, 'Falha ao atualizar chave de API'));
  }
};

/**
 * Permanently delete (revoke) an API Key — returns 204 No Content
 */
export const revokeApiKey = async (workspaceId: number, keyId: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/api-keys/${keyId}`, {
      headers: wsHeaders(workspaceId),
    });
  } catch (error) {
    throw new Error(extractErrorMessage(error as AxiosError, 'Falha ao revogar chave de API'));
  }
};

export default { listApiKeys, createApiKey, updateApiKey, revokeApiKey };
